// /opt/ptg01-v1/front-extranet/components/QuickActionsSheet.tsx
// Sheet d'actions rapides globale — accessible via le bouton + de la TopBar
// sur les pages /, /cras, /compte-activite, /notifications.
//
// Affiche un menu de navigation rapide vers les workflows clés du consultant :
// CRA, missions, compte d'activité, frais refacturables, frais ponctuels,
// messages, mon profil, aide. (Refonte des actions rapides : lot L1, décision 6.)
'use client';

import React, { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { X, CalendarCheck, Briefcase, Wallet, Receipt, FileText, Bell, User as UserIcon, HelpCircle, ChevronRight, Camera } from 'lucide-react';

interface Props {
    open: boolean;
    onClose: () => void;
}

// Réutilisable : clé localStorage de l'onboarding CRA (pour le reset du tour produit).
const CRA_ONBOARDING_STORAGE_KEY = 'nx-extranet-cra-onboarding-seen-v1';

interface Action {
    icon: React.ReactNode;
    iconBg: string;
    label: string;
    sub: string;
    onClick: () => void;
}

export function QuickActionsSheet({ open, onClose }: Props) {
    const router = useRouter();

    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    const go = (path: string) => { onClose(); router.push(path); };
    const restartCraTour = () => {
        try { window.localStorage.removeItem(CRA_ONBOARDING_STORAGE_KEY); } catch {}
        onClose();
        router.push('/cras');
        setTimeout(() => window.location.reload(), 100);
    };

    const actions: Action[] = [
        {
            icon: <Camera className="w-4 h-4" />, iconBg: 'bg-[#000091] text-white',
            label: 'Scanner un ticket', sub: 'Une photo suffit : Nexvia lit le montant pour vous',
            onClick: () => go('/depenses/scanner'),
        },
        {
            icon: <CalendarCheck className="w-4 h-4" />, iconBg: 'bg-[#000091]/10 text-[#000091]',
            label: 'Mes CRAs', sub: 'Saisie et soumission mensuelle',
            onClick: () => go('/cras'),
        },
        {
            icon: <Briefcase className="w-4 h-4" />, iconBg: 'bg-[#000091]/10 text-[#000091]',
            label: 'Mes missions', sub: 'Contrats et clients en cours',
            onClick: () => go('/missions'),
        },
        {
            icon: <Wallet className="w-4 h-4" />, iconBg: 'bg-[#000091]/10 text-[#000091]',
            label: 'Compte d’activité', sub: 'Salaires, réserves, bulletins',
            onClick: () => go('/compte-activite'),
        },
        {
            icon: <Receipt className="w-4 h-4" />, iconBg: 'bg-[#000091]/10 text-[#000091]',
            label: 'Notes de frais refacturables', sub: 'Facturées au client en plus du TJM',
            onClick: () => go('/depenses/refacturees'),
        },
        {
            icon: <FileText className="w-4 h-4" />, iconBg: 'bg-[#000091]/10 text-[#000091]',
            label: 'Mes notes de frais', sub: 'Frais déduits du net (transport, repas…)',
            onClick: () => go('/depenses'),
        },
        {
            icon: <Bell className="w-4 h-4" />, iconBg: 'bg-[#000091]/10 text-[#000091]',
            label: 'Messages', sub: 'Notifications et rappels',
            onClick: () => go('/notifications'),
        },
        {
            icon: <UserIcon className="w-4 h-4" />, iconBg: 'bg-[#000091]/10 text-[#000091]',
            label: 'Mon profil', sub: 'Profil, coordonnées, mot de passe',
            onClick: () => go('/mon-compte'),
        },
        {
            icon: <HelpCircle className="w-4 h-4" />, iconBg: 'bg-emerald-50 text-emerald-700',
            label: 'Revoir la visite guidée CRA', sub: 'Tour produit — saisie et soumission',
            onClick: restartCraTour,
        },
    ];

    return (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center sm:justify-center" role="dialog" aria-modal="true">
            <button
                type="button"
                aria-label="Fermer"
                onClick={onClose}
                className="absolute inset-0 bg-slate-900/40 animate-[fade-in_180ms_ease-out]"
            />
            <div
                className="relative w-full sm:max-w-md max-h-[88vh] bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl animate-[sheet-up_220ms_cubic-bezier(0.16,1,0.3,1)] flex flex-col"
                style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            >
                <div className="flex justify-center pt-2 sm:hidden shrink-0">
                    <span className="w-10 h-1 rounded-full bg-slate-300" />
                </div>

                <div className="flex items-center justify-between px-5 pt-3 pb-2 shrink-0">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-[#000091]">Actions rapides</div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-9 h-9 -mr-2 inline-flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
                        aria-label="Fermer"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="px-2 pb-2 overflow-y-auto">
                    {actions.map((a, i) => (
                        <button
                            key={i}
                            type="button"
                            onClick={a.onClick}
                            className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-[#000091]/[0.03] active:scale-[0.99] text-left transition-colors"
                        >
                            <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${a.iconBg}`}>
                                {a.icon}
                            </span>
                            <span className="flex-1 min-w-0">
                                <span className="block text-[13px] font-semibold text-slate-900 truncate">{a.label}</span>
                                <span className="block text-[11px] text-slate-500 truncate">{a.sub}</span>
                            </span>
                            <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" strokeWidth={2.5} />
                        </button>
                    ))}
                </div>

                <div className="px-5 pt-1 pb-4 shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-full h-11 rounded-xl border-2 border-slate-200 text-slate-700 text-[13px] font-semibold hover:bg-slate-50"
                    >
                        Fermer
                    </button>
                </div>
            </div>
        </div>
    );
}
